// src/pages/Users/Users.tsx
import React from "react";
import Card, { CardHeader } from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import UserTable from "./components/UserTable";
import Pagination from "../../components/UI/Pagination";
import { useUsers } from "../../hooks/useUsers";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const Users: React.FC = () => {
  const {
    users,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    handleDeleteUser,
    refreshUsers,
    currentPage,
    totalPages,
    totalUsers,
    handlePageChange,
    isSearching,
  } = useUsers();

  // Show loading state for search
  const isLoading = loading || isSearching;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
        <p className="mt-2 text-gray-600">Manage all users in the system</p>
      </div>

      {/* Search and Actions */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Search by name, email, or username... (min. 3 characters)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm.length > 0 && searchTerm.length < 3 && (
              <p className="mt-1 text-xs text-yellow-600">
                Please enter at least 3 characters to search
              </p>
            )}
          </div>

          <div className="flex space-x-3">
            <Button variant="secondary" onClick={refreshUsers}>
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader
          title={`Users (${totalUsers})`}
          subtitle={`Showing ${users.length} of ${totalUsers} total users${
            searchTerm.length >= 3 ? " (search results)" : ""
          }`}
        />
        <UserTable
          users={users}
          loading={isLoading}
          onDelete={handleDeleteUser}
        />

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="mt-6 border-t border-gray-200 pt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default Users;
